import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Alert,
  useTheme,
  Fade,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import MapIcon from '@mui/icons-material/Map';
import SendIcon from '@mui/icons-material/Send';
import AOS from 'aos';
import 'aos/dist/aos.css';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const initialForm = { name: '', email: '', message: '' };

const validateEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const GOOGLE_MAPS_URL = 'https://maps.app.goo.gl/UZV6xsonCXBaZEmE8';

const contactInfo = [
  {
    icon: <EmailIcon />,
    label: 'Email',
    value: 'contact@bixbite.ai',
    href: 'mailto:contact@bixbite.ai',
  },
  {
    icon: <LocationOnIcon />,
    label: 'Address',
    value: 'Cohort Coworking, Kondapur, Hyderabad',
  },
  {
    icon: <PhoneIcon />,
    label: 'Phone',
    value: '+91 9538474567',
    href: 'tel:+919538474567',
  },
];

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: '', msg: '' });
  const [submitting, setSubmitting] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic',
      disable: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    });
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
    setStatus({ type: '', msg: '' });
  };

  const validate = () => {
    let temp = {};
    if (!form.name.trim()) temp.name = 'Name is required';
    if (!form.email.trim()) temp.email = 'Email is required';
    else if (!validateEmail(form.email)) temp.email = 'Invalid email address';
    if (!form.message.trim()) temp.message = 'Message is required';
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    try {
      const response = await axios.post(getApiUrl('api/send-email'), form);
      if (response.data.success) {
        setStatus({
          type: 'success',
          msg: 'Thank you for contacting us! We will get back to you soon.',
        });
        setForm(initialForm);
      } else {
        setStatus({ type: 'error', msg: 'Failed to send message. Try again later.' });
      }
    } catch (err) {
      setStatus({ type: 'error', msg: 'Something went wrong. Try again later.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 6 },
          borderRadius: '28px',
          border: '1px solid rgba(0, 121, 107, 0.1)',
          bgcolor: 'white',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Background Decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'rgba(0, 121, 107, 0.03)',
            filter: 'blur(60px)',
            pointerEvents: 'none',
          }}
        />

        <Grid container spacing={6} alignItems="stretch" sx={{ position: 'relative' }}>
          {/* Contact Form */}
          <Grid item xs={12} md={7}>
            <Fade in timeout={600}>
              <Box data-aos="fade-right">
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    mb: 2,
                    background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Contact Us
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ 
                    color: theme.palette.text.secondary, 
                    mb: 4,
                    fontSize: '1.05rem',
                    lineHeight: 1.7,
                  }}
                >
                  Have a question or want to learn more about Bixbite AI? Fill out the form and our team will get in touch!
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ 
                    color: theme.palette.text.secondary, 
                    mb: 4,
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                    fontStyle: 'italic',
                  }}
                >
                  For any issues in LoCoPilot or any upgrade in LoCoPilot, you can write to us.
                </Typography>

                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  autoComplete="off"
                  sx={{ width: '100%' }}
                >
                  <Stack spacing={3}>
                    <TextField
                      label="Name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      error={!!errors.name}
                      helperText={errors.name}
                      fullWidth
                      required
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '14px',
                          bgcolor: '#f8fffe',
                          transition: 'all 0.3s ease',
                          '& fieldset': {
                            borderColor: 'rgba(0, 121, 107, 0.15)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(0, 121, 107, 0.3)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#00796b',
                          },
                          '&.Mui-focused': {
                            boxShadow: '0 4px 16px -4px rgba(0, 121, 107, 0.15)',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#00796b',
                        },
                      }}
                    />
                    <TextField
                      label="Email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      error={!!errors.email}
                      helperText={errors.email}
                      fullWidth
                      required
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '14px',
                          bgcolor: '#f8fffe',
                          transition: 'all 0.3s ease',
                          '& fieldset': {
                            borderColor: 'rgba(0, 121, 107, 0.15)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(0, 121, 107, 0.3)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#00796b',
                          },
                          '&.Mui-focused': {
                            boxShadow: '0 4px 16px -4px rgba(0, 121, 107, 0.15)',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#00796b',
                        },
                      }}
                    />
                    <TextField
                      label="Message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      error={!!errors.message}
                      helperText={errors.message}
                      fullWidth
                      required
                      multiline
                      minRows={8}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '14px',
                          bgcolor: '#f8fffe',
                          transition: 'all 0.3s ease',
                          '& fieldset': {
                            borderColor: 'rgba(0, 121, 107, 0.15)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(0, 121, 107, 0.3)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#00796b',
                          },
                          '&.Mui-focused': {
                            boxShadow: '0 4px 16px -4px rgba(0, 121, 107, 0.15)',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#00796b',
                        },
                      }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={submitting}
                      endIcon={<SendIcon />}
                      sx={{
                        py: 1.8,
                        px: 4,
                        fontWeight: 700,
                        fontSize: '1.05rem',
                        borderRadius: '14px',
                        background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                        boxShadow: '0 8px 24px -8px rgba(0, 77, 64, 0.4)',
                        alignSelf: 'flex-start',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: '0 12px 32px -8px rgba(0, 77, 64, 0.5)',
                        },
                        '&:disabled': {
                          background: 'rgba(0, 121, 107, 0.3)',
                        },
                      }}
                    >
                      {submitting ? 'Sending...' : 'Send Message'}
                    </Button>

                    {status.msg && (
                      <Alert 
                        severity={status.type} 
                        sx={{ 
                          borderRadius: '12px',
                          '& .MuiAlert-icon': {
                            alignItems: 'center',
                          },
                        }}
                      >
                        {status.msg}
                      </Alert>
                    )}
                  </Stack>
                </Box>
              </Box>
            </Fade>
          </Grid>

          {/* Contact Details & Map */}
          <Grid item xs={12} md={5}>
            <Fade in timeout={800}>
              <Box 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                data-aos="fade-left"
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: '24px',
                    background: 'linear-gradient(135deg, #f8fffe 0%, #e0f7f4 100%)',
                    border: '1px solid rgba(0, 121, 107, 0.1)',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#004d40',
                      mb: 3,
                    }}
                  >
                    Company Details
                  </Typography>

                  <Stack spacing={2.5} sx={{ mb: 4 }}>
                    {contactInfo.map((info, idx) => (
                      <Box
                        key={idx}
                        component={info.href ? 'a' : 'div'}
                        href={info.href}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 2,
                          borderRadius: '14px',
                          bgcolor: 'white',
                          border: '1px solid rgba(0, 121, 107, 0.08)',
                          textDecoration: 'none',
                          transition: 'all 0.3s ease',
                          cursor: info.href ? 'pointer' : 'default',
                          '&:hover': info.href ? {
                            transform: 'translateX(4px)',
                            boxShadow: '0 4px 16px -4px rgba(0, 77, 64, 0.15)',
                          } : {},
                        }}
                      >
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            flexShrink: 0,
                            '& svg': { fontSize: 22 },
                          }}
                        >
                          {info.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#1a2f2c',
                              fontWeight: 600,
                            }}
                          >
                            {info.value}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>

                  {/* Map */}
                  <Box
                    sx={{
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 20px -8px rgba(0, 77, 64, 0.2)',
                      border: '1px solid rgba(0, 121, 107, 0.1)',
                      flex: 1,
                      minHeight: 200,
                    }}
                  >
                    <iframe
                      title="Cohort Coworking, Kondapur, Hyderabad"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.0808918576295!2d78.36269341121218!3d17.45584198337401!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb9366a7909207%3A0xa5ef612527c881f8!2sCOHORT%20Coworking%20%40%20Kondapur!5e0!3m2!1sen!2sin!4v1754069448956!5m2!1sen!2sin"
                      width="100%"
                      height="100%"
                      style={{
                        border: 0,
                        minHeight: 180,
                      }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </Box>

                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<MapIcon />}
                    sx={{
                      mt: 2,
                      py: 1.2,
                      fontWeight: 600,
                      borderRadius: '12px',
                      borderWidth: 2,
                      borderColor: '#00796b',
                      color: '#00796b',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderWidth: 2,
                        background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                        color: 'white',
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => window.open(GOOGLE_MAPS_URL, '_blank', 'noopener,noreferrer')}
                    aria-label="Get Directions"
                  >
                    Get Directions
                  </Button>
                </Paper>
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Contact;
